import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import { currency, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Trash2, ShoppingBag } from "lucide-react";

export default function CartPage() {
  const { items, removeItem, subtotal } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const shipping = subtotal >= 800 || subtotal === 0 ? 0 : 99;
  const total = subtotal + shipping;

  const goCheckout = () => {
    nav("/checkout");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-12 h-12 mx-auto text-neutral-300" />
        <h1 className="font-display text-4xl tracking-tighter mt-6">Tu carrito está vacío</h1>
        <p className="text-neutral-500 mt-3">Explora el catálogo y personaliza tu primer producto.</p>
        <Button asChild className="brutal-btn bg-black hover:bg-[#FF3B30] text-white mt-6 h-12 px-6">
          <Link to="/tienda">Ir a la tienda</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="cart-page">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase text-neutral-500">/carrito</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-1">Tu Carrito</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {items.map((it) => (
            <div key={it.cart_id} className="brutal-card p-4 flex gap-4" data-testid={`cart-item-${it.cart_id}`}>
              <div className="w-24 h-24 flex-shrink-0 bg-neutral-100 relative overflow-hidden">
                {it.color_hex && (
                  <div className="absolute inset-0" style={{ backgroundColor: it.color_hex, opacity: 0.9 }} />
                )}
                <img
                  src={it.product_image}
                  alt={it.product_name}
                  className="w-full h-full object-cover"
                  style={{ mixBlendMode: it.color_hex ? "multiply" : "normal" }}
                />
                {it.design_url && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      top: `${it.design_position?.top ?? it.mockup?.top ?? 30}%`,
                      left: `${it.design_position?.left ?? it.mockup?.left ?? 30}%`,
                      width: `${it.design_position?.width ?? it.mockup?.width ?? 40}%`,
                      height: `${it.design_position?.height ?? it.mockup?.height ?? 40}%`,
                      transform: `rotate(${it.design_position?.rotation ?? 0}deg)`,
                      transformOrigin: "center",
                    }}
                  >
                    <img src={`${API}/files/${it.design_url}`} alt="d" className="w-full h-full object-contain" style={{ mixBlendMode: "multiply" }} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono uppercase text-neutral-500">Producto</div>
                <div className="font-medium">{it.product_name}</div>
                <div className="text-xs text-neutral-600 mt-1 font-mono">
                  {it.color_label && <>Color: {it.color_label} · </>}
                  {it.size_label && <>Talla: {it.size_label} · </>}
                  Cant: {it.quantity}
                </div>
                {it.printing_method_label && (
                  <div className="text-xs text-neutral-600 mt-1 font-mono">Impresión: {it.printing_method_label}</div>
                )}
                {it.with_design_service && (
                  <div className="text-xs text-purple-700 mt-1 font-mono">+ Servicio de diseño</div>
                )}
                {it.design_filename && (
                  <div className="text-xs text-neutral-500 mt-1 font-mono truncate">Diseño: {it.design_filename}</div>
                )}
                {it.notes && <div className="text-xs text-neutral-500 mt-1 italic truncate">"{it.notes}"</div>}
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeItem(it.cart_id)} className="p-1 hover:text-red-600" data-testid={`cart-remove-${it.cart_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="font-display text-lg">{currency(it.line_total)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="brutal-card p-6 lg:sticky lg:top-24">
            <div className="text-xs font-mono uppercase text-neutral-500">Resumen</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{currency(subtotal)}</span></div>
              <div className="flex justify-between"><span>Envío</span><span>{shipping === 0 ? "Gratis" : currency(shipping)}</span></div>
              <div className="h-px bg-neutral-200 my-3" />
              <div className="flex justify-between font-display text-2xl"><span>Total</span><span>{currency(total)}</span></div>
            </div>
            <Button
              onClick={goCheckout}
              size="lg"
              className="w-full h-12 mt-6 brutal-btn bg-black hover:bg-[#FF3B30] text-white"
              data-testid="cart-checkout-btn"
            >
              Continuar al checkout
            </Button>
            <Link to="/tienda" className="block text-center text-xs font-mono uppercase text-neutral-500 hover:text-black mt-4">
              ← Seguir comprando
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
